// The standalone metronome (#413): a practice tool that runs without a tune.
// The pure half lives here — the page's settings, their guard, and the maths
// the scheduler asks for. The click itself is the band's metronome channel
// (metronome.ts + the player), so its voice, accents and subdivision are one
// setting shared with the workbench.

import { TEMPO_MAX, TEMPO_MIN } from '@/state/tunePlayback';

export { gapClickSounds } from '@/audio/metronome';

export interface GapClick {
  enabled: boolean;
  /** Bars that click. */
  on: number;
  /** Bars of silence after them, where you keep the time yourself. */
  off: number;
}

export interface TempoRamp {
  enabled: boolean;
  /** Starting tempo, bpm. */
  from: number;
  /** Target tempo, bpm; the ramp holds here. */
  to: number;
  /** Change per step, bpm (a magnitude; direction comes from from → to). */
  step: number;
  /** Bars at each tempo before the next step. */
  bars: number;
}

export interface PracticeMetronome {
  bpm: number;
  beatsPerBar: number;
  /** The click sounds; off leaves a drum loop keeping time alone. */
  click: boolean;
  /** A drum loop to play instead of, or with, the click. */
  drumLoopKey: string | null;
  gap: GapClick;
  ramp: TempoRamp;
  /** Stop after this many minutes; 0 runs until Stop. */
  sessionMinutes: number;
}

export const METRE_CHOICES = [2, 3, 4, 5, 6, 7] as const;
export const SESSION_CHOICES = [0, 5, 10, 15, 20, 30] as const;

export const DEFAULT_PRACTICE_METRONOME: PracticeMetronome = {
  bpm: 100,
  beatsPerBar: 4,
  click: true,
  drumLoopKey: null,
  gap: { enabled: false, on: 2, off: 2 },
  ramp: { enabled: false, from: 80, to: 120, step: 4, bars: 4 },
  sessionMinutes: 0,
};

const clampTempo = (n: number) => Math.max(TEMPO_MIN, Math.min(TEMPO_MAX, Math.round(n)));

function wholeIn(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(min, Math.min(max, Math.round(value)))
    : fallback;
}

function tempoOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? clampTempo(value) : fallback;
}

/** A persisted blob is untrusted: every field falls back to its default. */
export function normalizePracticeMetronome(input: unknown): PracticeMetronome {
  const raw = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const d = DEFAULT_PRACTICE_METRONOME;
  const gap = (raw.gap && typeof raw.gap === 'object' ? raw.gap : {}) as Record<string, unknown>;
  const ramp = (raw.ramp && typeof raw.ramp === 'object' ? raw.ramp : {}) as Record<
    string,
    unknown
  >;
  return {
    bpm: tempoOr(raw.bpm, d.bpm),
    beatsPerBar: wholeIn(raw.beatsPerBar, 1, 12, d.beatsPerBar),
    click: typeof raw.click === 'boolean' ? raw.click : d.click,
    drumLoopKey:
      typeof raw.drumLoopKey === 'string' && raw.drumLoopKey ? raw.drumLoopKey : d.drumLoopKey,
    gap: {
      enabled: typeof gap.enabled === 'boolean' ? gap.enabled : d.gap.enabled,
      on: wholeIn(gap.on, 1, 16, d.gap.on),
      off: wholeIn(gap.off, 1, 16, d.gap.off),
    },
    ramp: {
      enabled: typeof ramp.enabled === 'boolean' ? ramp.enabled : d.ramp.enabled,
      from: tempoOr(ramp.from, d.ramp.from),
      to: tempoOr(ramp.to, d.ramp.to),
      step: wholeIn(ramp.step, 1, 40, d.ramp.step),
      bars: wholeIn(ramp.bars, 1, 32, d.ramp.bars),
    },
    sessionMinutes: wholeIn(raw.sessionMinutes, 0, 120, d.sessionMinutes),
  };
}

/**
 * Tempo for a 1-based block of `ramp.bars` bars: `from`, then one `step`
 * toward `to` per block, holding at `to`. Works up (80 → 120) or down.
 */
export function rampTempoForBlock(block: number, ramp: TempoRamp): number {
  const steps = Math.max(0, Math.floor(block) - 1);
  const step = Math.max(1, Math.abs(ramp.step));
  const dir = Math.sign(ramp.to - ramp.from);
  const bpm = ramp.from + steps * step * dir;
  return clampTempo(dir >= 0 ? Math.min(ramp.to, bpm) : Math.max(ramp.to, bpm));
}

/**
 * How many bars one pass of the practice form spans. The scheduler loops a
 * form; the ramp steps once per pass, so a ramp's form is its block, and a
 * drum loop longer than that block rounds it up to a whole number of loops —
 * a form shorter than its loop would restart the kit every pass.
 */
export function practiceFormBars(settings: PracticeMetronome, loopBars: number | null): number {
  const unit = Math.max(1, Math.round(loopBars ?? 1));
  const base = settings.ramp.enabled ? settings.ramp.bars : unit;
  return Math.ceil(Math.max(1, base) / unit) * unit;
}

/** "12:05" for a count of seconds. */
export function clockText(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
